import { queryOptions } from "@tanstack/react-query";
import { api } from "./client";
import { payload } from "./envelope";
import { ApiError, errorCode, errorMessage } from "./errors";
import type { components } from "./schema";

export type CatalogService = components["schemas"]["CatalogService"];
export type CatalogServiceRequest = components["schemas"]["CatalogServiceRequest"];
export type UpdateCatalogServiceRequest =
  components["schemas"]["UpdateCatalogServiceRequest"];

/**
 * Catálogo de serviços do estabelecimento. A lista vem completa (não
 * paginada) e inclui os inativos (`active: false` = soft-delete) — quem
 * decide como mostrar é a tela.
 */
export function servicesQuery(establishmentId: string) {
  return queryOptions({
    queryKey: ["establishment", establishmentId, "services"] as const,
    queryFn: () =>
      payload(
        api.GET("/establishments/{id}/services", {
          params: { path: { id: establishmentId } },
        }),
      ),
  });
}

/**
 * `deposit_required: true` pode dar `409` quando o estabelecimento não tem
 * como cobrar sinal (Stripe/deposit-policy) — o erro chega pronto pra exibir.
 */
export function createService(
  establishmentId: string,
  body: CatalogServiceRequest,
): Promise<CatalogService> {
  return payload(
    api.POST("/establishments/{id}/services", {
      params: { path: { id: establishmentId } },
      body,
    }),
  );
}

/**
 * Update parcial: campo ausente preserva o valor atual. Não aceita `active`
 * — desativar é só pelo DELETE (soft-delete) e reativar é só pelo
 * `activateService`.
 */
export function updateService(
  id: string,
  body: UpdateCatalogServiceRequest,
): Promise<CatalogService> {
  return payload(api.PUT("/services/{id}", { params: { path: { id } }, body }));
}

/**
 * DELETE é o soft-delete (`active = false`): devolve 204 sem corpo, então
 * não passa pelo `payload` (que exige `data`) — aqui só interessa o erro.
 * A volta do inativo é `activateService`.
 */
export async function deleteService(id: string): Promise<void> {
  const { error } = await api.DELETE("/services/{id}", {
    params: { path: { id } },
  });
  if (error) {
    throw new ApiError(errorCode(error) ?? "unknown_error", errorMessage(error));
  }
}

/**
 * Caminho de volta do soft-delete: o serviço volta exatamente como estava
 * (preço, duração, sinal e vínculos), não como foi criado. Idempotente.
 * Serviço com `deposit_required` passa pela checagem de Stripe/deposit-policy
 * de novo — `409` se a conta saiu do ar enquanto ele estava inativo.
 */
export function activateService(serviceId: string): Promise<CatalogService> {
  return payload(
    api.POST("/services/{id}/activate", { params: { path: { id: serviceId } } }),
  );
}

/**
 * IDs dos profissionais que fazem esse serviço. Lista vazia = qualquer
 * profissional do estabelecimento faz — não é "ninguém atribuído".
 */
export function serviceProfessionalsQuery(serviceId: string) {
  return queryOptions({
    queryKey: ["service", serviceId, "professionals"] as const,
    queryFn: () =>
      payload(
        api.GET("/services/{id}/professionals", {
          params: { path: { id: serviceId } },
        }),
      ),
  });
}

/**
 * Substitui a lista INTEIRA de vínculos (não faz diff/união) — monte a lista
 * final na tela e mande completa. Lista vazia = volta ao padrão "qualquer
 * profissional faz esse serviço".
 */
export async function setServiceProfessionals(
  serviceId: string,
  professionalIds: string[],
): Promise<void> {
  await payload(
    api.PUT("/services/{id}/professionals", {
      params: { path: { id: serviceId } },
      body: { professional_ids: professionalIds },
    }),
  );
}
